import React from "react";
import { useEffect, useState } from 'react';
import { Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface CreativeFiltersProps {
  selectedType: string;
  onTypeChange: (value: string) => void;
  searchTerm: string;
  onSearchChange: (value: string) => void;
  selectedRatio: string;
  onRatioChange: (value: string) => void;
  groupTypeFilter: string;
  onGroupTypeFilterChange: (value: string) => void;
}

const ASPECT_RATIO_OPTIONS = [
  { value: 'all', label: 'All Ratios' },
  { value: '1:1 (Square)', label: '1:1 (Square)' },
  { value: '9:16 (Vertical)', label: '9:16 (Vertical)' },
  { value: '4:5', label: '4:5' },
  { value: '16:9', label: '16:9' },
  { value: '1.91:1 (Link)', label: '1.91:1 (Link)' },
  { value: 'Other', label: 'Other' },
];

const CreativeFilters = ({
  selectedType,
  onTypeChange,
  searchTerm,
  onSearchChange,
  selectedRatio,
  onRatioChange,
  groupTypeFilter,
  onGroupTypeFilterChange,
}: CreativeFiltersProps) => {
  const [localSearch, setLocalSearch] = useState(searchTerm);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      onSearchChange(localSearch);
    }, 300);

    return () => clearTimeout(timer);
  }, [localSearch, onSearchChange]);

  // Sync local search with external state
  useEffect(() => {
    setLocalSearch(searchTerm);
  }, [searchTerm]);

  return (
    <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:flex-wrap">

      {/* Asset Type Filter */}
      <div className="w-full sm:w-auto sm:min-w-[140px]">
        <Select value={selectedType} onValueChange={onTypeChange}>
          <SelectTrigger className="bg-background">
            <SelectValue placeholder="All Types" />
          </SelectTrigger>
          <SelectContent className="bg-popover">
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="image">Image</SelectItem>
            <SelectItem value="video">Video</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Aspect Ratio Filter */}
      <div className="w-full sm:w-auto sm:min-w-[160px]">
        <Select value={selectedRatio} onValueChange={onRatioChange}>
          <SelectTrigger className="bg-background">
            <SelectValue placeholder="All Ratios" />
          </SelectTrigger>
          <SelectContent className="bg-popover">
            {ASPECT_RATIO_OPTIONS.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Group Type Filter */}
      <div className="w-full sm:w-auto sm:min-w-[140px]">
        <Select value={groupTypeFilter} onValueChange={onGroupTypeFilterChange}>
          <SelectTrigger className="bg-background">
            <SelectValue placeholder="Group Type" />
          </SelectTrigger>
          <SelectContent className="bg-popover">
            <SelectItem value="all">All Groups</SelectItem>
            <SelectItem value="single">Singles</SelectItem>
            <SelectItem value="pair">Pairs</SelectItem>
            <SelectItem value="triple">Triples</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Search Input */}
      <div className="relative w-full sm:w-auto sm:flex-1 sm:min-w-[200px] sm:max-w-[300px]">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder="Search by filename..."
          value={localSearch}
          onChange={(e) => setLocalSearch(e.target.value)}
          className="pl-9 bg-background"
        />
      </div>
    </div>
  );
};

export default CreativeFilters;
